import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, setDoc, collection, getDocs, deleteDoc, updateDoc } from 'firebase/firestore';
import { FaArrowLeft, FaTrash, FaToggleOn, FaToggleOff, FaPlus } from 'react-icons/fa';
import DelayRender from './DelayRender';
import './AdminStore.css';

const AdminStore = () => {
  const [itemData, setItemData] = useState({
    category: '',
    description: '',
    forSale: true,
    imageUrl: '',
    name: '',
    price: 0,
    sellPrice: 0,
    currency: 'money',
    craftable: false,
    sellable: true,
    keepCraft: false,
  });
  const [documentId, setDocumentId] = useState('');
  const [items, setItems] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemData, setEditingItemData] = useState({});
  const [recipes, setRecipes] = useState([]);
  const [recipeData, setRecipeData] = useState({
    craftedItems: [{ itemId: '', quantity: 1 }],
    craftTime: 0,
    requiredItems: [{ itemId: '', quantity: 1 }],
  });
  const [editingRecipeId, setEditingRecipeId] = useState(null);
  const [showNewItemForm, setShowNewItemForm] = useState(false);
  const [showNewRecipeForm, setShowNewRecipeForm] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setItemData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : name === 'price' || name === 'sellPrice' ? parseFloat(value) : value,
    }));
  };

  const handleRecipeChange = (e, index, arrayName) => {
    const { name, value } = e.target;
    if (arrayName === 'craftedItems' || arrayName === 'requiredItems') {
      const newArray = [...recipeData[arrayName]];
      const key = name.split('-')[1];
      newArray[index][key] = key === 'quantity' ? parseInt(value, 10) : value;
      setRecipeData((prevData) => ({
        ...prevData,
        [arrayName]: newArray,
      }));
    } else {
      setRecipeData((prevData) => ({
        ...prevData,
        [name]: name === 'craftTime' ? parseInt(value, 10) : value,
      }));
    }
  };

  const addCraftedItem = () => {
    setRecipeData((prevData) => ({
      ...prevData,
      craftedItems: [...prevData.craftedItems, { itemId: '', quantity: 1 }],
    }));
  };

  const removeCraftedItem = (index) => {
    setRecipeData((prevData) => ({
      ...prevData,
      craftedItems: prevData.craftedItems.filter((_, idx) => idx !== index),
    }));
  };

  const addRequiredItem = () => {
    setRecipeData((prevData) => ({
      ...prevData,
      requiredItems: [...prevData.requiredItems, { itemId: '', quantity: 1 }],
    }));
  };

  const removeRequiredItem = (index) => {
    setRecipeData((prevData) => ({
      ...prevData,
      requiredItems: prevData.requiredItems.filter((_, idx) => idx !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (documentId.trim() === '') {
      alert('Document ID (Store Name) is required.');
      return;
    }

    const db = getFirestore();
    try {
      await setDoc(doc(db, 'items', documentId), {
        ...itemData,
        price: parseFloat(itemData.price),
        sellPrice: parseFloat(itemData.sellPrice),
      });
      alert('Item added successfully!');
      setDocumentId('');
      setItemData({
        category: '',
        description: '',
        forSale: true,
        imageUrl: '',
        name: '',
        price: 0,
        sellPrice: 0,
        currency: 'money',
        craftable: false,
        sellable: true,
        keepCraft: false,
      });
      fetchItems();
    } catch (error) {
      console.error('Error adding item:', error);
      alert('Failed to add item. Please try again.');
    }
  };

  const handleRecipeSubmit = async (e, isEditing) => {
    e.preventDefault();
    const db = getFirestore();
    try {
      if (isEditing && editingRecipeId) {
        await updateDoc(doc(db, 'craftingRecipes', editingRecipeId), recipeData);
        setEditingRecipeId(null);
      } else {
        await setDoc(doc(collection(db, 'craftingRecipes')), recipeData);
      }
      alert('Recipe saved successfully!');
      setRecipeData({
        craftedItems: [{ itemId: '', quantity: 1 }],
        craftTime: 0,
        requiredItems: [{ itemId: '', quantity: 1 }],
      });
      fetchRecipes();
    } catch (error) {
      console.error('Error saving recipe:', error);
      alert('Failed to save recipe. Please try again.');
    }
  };

  const fetchItems = async () => {
    const db = getFirestore();
    const itemsCollection = collection(db, 'items');
    const itemsSnapshot = await getDocs(itemsCollection);
    const itemsList = itemsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setItems(itemsList);
  };

  const fetchRecipes = async () => {
    const db = getFirestore();
    const recipesCollection = collection(db, 'craftingRecipes');
    const recipesSnapshot = await getDocs(recipesCollection);
    const recipesList = recipesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setRecipes(recipesList);
  };

  useEffect(() => {
    fetchItems();
    fetchRecipes();
  }, []);

  const toggleForSale = async (id, currentStatus) => {
    const db = getFirestore();
    try {
      await updateDoc(doc(db, 'items', id), {
        forSale: !currentStatus,
      });
      fetchItems();
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const deleteItem = async (id) => {
    const db = getFirestore();
    try {
      await deleteDoc(doc(db, 'items', id));
      fetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const deleteRecipe = async (id) => {
    const db = getFirestore();
    try {
      await deleteDoc(doc(db, 'craftingRecipes', id));
      fetchRecipes();
    } catch (error) {
      console.error('Error deleting recipe:', error);
    }
  };

  const handleEditChange = (e) => {
    const { name, value, checked, type } = e.target;
    setEditingItemData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : name === 'price' || name === 'sellPrice' ? parseFloat(value) : value,
    }));
  };

  const handleEditSubmit = async (id) => {
    const db = getFirestore();
    try {
      const { id: _, ...dataToUpdate } = editingItemData;
      await updateDoc(doc(db, 'items', id), {
        ...dataToUpdate,
        price: parseFloat(dataToUpdate.price),
        sellPrice: parseFloat(dataToUpdate.sellPrice),
      });
      alert('Item updated successfully!');
      setEditingItemId(null);
      fetchItems();
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Failed to update item. Please try again.');
    }
  };

  return (
    <div className="page">
      <div className="page-container admin-store-container">
        <div className="admin-store-header">
          <FaArrowLeft className="admin-store-back-arrow" onClick={() => navigate(-1)} />
          <h3 className="admin-store-title">Item Admin</h3>
        </div>
        <div className="items-list">
          <h3 className="admin-store-title">Shop Items</h3>
          <ul>
            {items.map((item) => (
              <li key={item.id}>
                {editingItemId === item.id ? (
                  <form
                    className="edit-item-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleEditSubmit(item.id);
                    }}
                  >
                    <label>
                      Name:
                      <input
                        type="text"
                        name="name"
                        value={editingItemData.name ?? item.name}
                        onChange={handleEditChange}
                        required
                      />
                    </label>
                    <label>
                      Category:
                      <input
                        type="text"
                        name="category"
                        value={editingItemData.category ?? item.category}
                        onChange={handleEditChange}
                        required
                      />
                    </label>
                    <label>
                      Description:
                      <input
                        type="text"
                        name="description"
                        value={editingItemData.description ?? item.description}
                        onChange={handleEditChange}
                        required
                      />
                    </label>
                    <label>
                      Price:
                      <input
                        type="number"
                        name="price"
                        value={editingItemData.price ?? item.price}
                        onChange={handleEditChange}
                        required
                      />
                    </label>
                    <label>
                      Sell Price:
                      <input
                        type="number"
                        name="sellPrice"
                        value={editingItemData.sellPrice ?? item.sellPrice}
                        onChange={handleEditChange}
                        required
                      />
                    </label>
                    <label>
                      Image URL:
                      <input
                        type="text"
                        name="imageUrl"
                        value={editingItemData.imageUrl ?? item.imageUrl}
                        onChange={handleEditChange}
                        required
                      />
                    </label>
                    <label>
                      Currency:
                      <select
                        name="currency"
                        value={editingItemData.currency ?? item.currency}
                        onChange={handleEditChange}
                        required
                      >
                        <option value="money">💴 Money</option>
                        <option value="hahumCoin">🪙 HahumCoin</option>
                      </select>
                    </label>
                    <label>
                      For Sale:
                      <input
                        type="checkbox"
                        name="forSale"
                        checked={editingItemData.forSale ?? item.forSale}
                        onChange={handleEditChange}
                      />
                    </label>
                    <label>
                      Craftable:
                      <input
                        type="checkbox"
                        name="craftable"
                        checked={editingItemData.craftable ?? item.craftable}
                        onChange={handleEditChange}
                      />
                    </label>
                    <label>
                      Sellable:
                      <input
                        type="checkbox"
                        name="sellable"
                        checked={editingItemData.sellable ?? item.sellable}
                        onChange={handleEditChange}
                      />
                    </label>
                    <label>
                      Keep on Craft:
                      <input
                        type="checkbox"
                        name="keepCraft"
                        checked={editingItemData.keepCraft ?? item.keepCraft}
                        onChange={handleEditChange}
                      />
                    </label>
                    <button type="submit">Save</button>
                    <button type="button" onClick={() => setEditingItemId(null)}>Cancel</button>
                  </form>
                ) : (
                  <>
                    <span>{item.name}</span>
                    <span> - {item.category}</span>
                    <span> - {item.currency === 'money' ? `💴 ${item.price}` : `🪙 ${item.price}`}</span>
                    <span className="toggle-sale" onClick={() => toggleForSale(item.id, item.forSale)}>
                      {item.forSale ? <FaToggleOn /> : <FaToggleOff />}
                    </span>
                    <FaTrash className="delete-icon" onClick={() => deleteItem(item.id)} />
                    <button onClick={() => {
                      setEditingItemId(item.id);
                      setEditingItemData(item);
                    }}>Edit</button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
        <button onClick={() => setShowNewItemForm(!showNewItemForm)} className="add-item-button">
          {showNewItemForm ? 'Cancel' : 'Add Item'} <FaPlus />
        </button>
        {showNewItemForm && (
          <form className="add-item-form" onSubmit={handleSubmit}>
            <label>
              Document ID:
              <input type="text" value={documentId} onChange={(e) => setDocumentId(e.target.value)} required />
            </label>
            <label>
              Category:
              <input type="text" name="category" value={itemData.category} onChange={handleChange} required />
            </label>
            <label>
              Description:
              <input type="text" name="description" value={itemData.description} onChange={handleChange} required />
            </label>
            <label>
              For Sale:
              <input type="checkbox" name="forSale" checked={itemData.forSale} onChange={handleChange} />
            </label>
            <label>
              Image URL:
              <input type="text" name="imageUrl" value={itemData.imageUrl} onChange={handleChange} required />
            </label>
            <label>
              Name:
              <input type="text" name="name" value={itemData.name} onChange={handleChange} required />
            </label>
            <label>
              Price:
              <input type="number" name="price" value={itemData.price} onChange={handleChange} required />
            </label>
            <label>
              Sell Price:
              <input type="number" name="sellPrice" value={itemData.sellPrice} onChange={handleChange} required />
            </label>
            <label>
              Currency:
              <select name="currency" value={itemData.currency} onChange={handleChange} required>
                <option value="money">💴 Money</option>
                <option value="hahumCoin">🪙 HahumCoin</option>
              </select>
            </label>
            <label>
              Craftable:
              <input type="checkbox" name="craftable" checked={itemData.craftable} onChange={handleChange} />
            </label>
            <label>
              Sellable:
              <input type="checkbox" name="sellable" checked={itemData.sellable} onChange={handleChange} />
            </label>
            <label>
              Keep Craft:
              <input type="checkbox" name="keepCraft" checked={itemData.keepCraft} onChange={handleChange} />
            </label>
            <button type="submit">Add Item</button>
          </form>
        )}
        <div className="recipes-list">
          <h3 className="admin-store-title">Crafting Recipes</h3>
          <ul>
            {recipes.map((recipe) => (
              <li key={recipe.id}>
                {editingRecipeId === recipe.id ? (
                  <form
                    className="edit-recipe-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleRecipeSubmit(e, true);
                    }}
                  >
                    {recipeData.craftedItems.map((craftedItem, index) => (
                      <div key={`crafted-${index}`}>
                        <label>
                          Crafted Item ID:
                          <input
                            type="text"
                            name={`craftedItem-itemId-${index}`}
                            value={craftedItem.itemId}
                            onChange={(e) => handleRecipeChange(e, index, 'craftedItems')}
                            required
                          />
                        </label>
                        <label>
                          Quantity:
                          <input
                            type="number"
                            name={`craftedItem-quantity-${index}`}
                            value={craftedItem.quantity}
                            onChange={(e) => handleRecipeChange(e, index, 'craftedItems')}
                            required
                          />
                        </label>
                        <button type="button" onClick={() => removeCraftedItem(index)}>Remove</button>
                      </div>
                    ))}
                    <button type="button" onClick={addCraftedItem}>Add Crafted Item</button>
                    <label>
                      Craft Time (seconds):
                      <input
                        type="number"
                        name="craftTime"
                        value={recipeData.craftTime}
                        onChange={(e) => handleRecipeChange(e, -1)}
                        required
                      />
                    </label>
                    {recipeData.requiredItems.map((requiredItem, index) => (
                      <div key={`required-${index}`}>
                        <label>
                          Required Item ID:
                          <input
                            type="text"
                            name={`requiredItem-itemId-${index}`}
                            value={requiredItem.itemId}
                            onChange={(e) => handleRecipeChange(e, index, 'requiredItems')}
                            required
                          />
                        </label>
                        <label>
                          Quantity:
                          <input
                            type="number"
                            name={`requiredItem-quantity-${index}`}
                            value={requiredItem.quantity}
                            onChange={(e) => handleRecipeChange(e, index, 'requiredItems')}
                            required
                          />
                        </label>
                        <button type="button" onClick={() => removeRequiredItem(index)}>Remove</button>
                      </div>
                    ))}
                    <button type="button" onClick={addRequiredItem}>Add Required Item</button>
                    <button type="submit">Save Recipe</button>
                    <button type="button" onClick={() => setEditingRecipeId(null)}>Cancel</button>
                  </form>
                ) : (
                  <>
                    <span>{recipe.craftedItems.map((item) => `${item.quantity}x ${item.itemId}`).join(', ')}</span>
                    <span> - Craft Time: {recipe.craftTime} seconds</span>
                    <span> - Required Items: {recipe.requiredItems.map((item) => `${item.quantity}x ${item.itemId}`).join(', ')}</span>
                    <FaTrash className="delete-icon" onClick={() => deleteRecipe(recipe.id)} />
                    <button onClick={() => {
                      setEditingRecipeId(recipe.id);
                      setRecipeData(recipe);
                    }}>Edit</button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
        <button onClick={() => setShowNewRecipeForm(!showNewRecipeForm)} className="add-recipe-button">
          {showNewRecipeForm ? 'Cancel' : 'Add Recipe'} <FaPlus />
        </button>
        {showNewRecipeForm && (
          <form className="add-recipe-form" onSubmit={(e) => handleRecipeSubmit(e, false)}>
            {recipeData.craftedItems.map((craftedItem, index) => (
              <div key={`crafted-${index}`}>
                <label>
                  Crafted Item ID:
                  <input type="text" name={`craftedItem-itemId-${index}`} value={craftedItem.itemId} onChange={(e) => handleRecipeChange(e, index, 'craftedItems')} required />
                </label>
                <label>
                  Quantity:
                  <input type="number" name={`craftedItem-quantity-${index}`} value={craftedItem.quantity} onChange={(e) => handleRecipeChange(e, index, 'craftedItems')} required />
                </label>
                <button type="button" onClick={() => removeCraftedItem(index)}>Remove</button>
              </div>
            ))}
            <button type="button" onClick={addCraftedItem}>Add Crafted Item</button>
            <label>
              Craft Time (seconds):
              <input type="number" name="craftTime" value={recipeData.craftTime} onChange={(e) => handleRecipeChange(e, -1)} required />
            </label>
            {recipeData.requiredItems.map((requiredItem, index) => (
              <div key={`required-${index}`}>
                <label>
                  Required Item ID:
                  <input type="text" name={`requiredItem-itemId-${index}`} value={requiredItem.itemId} onChange={(e) => handleRecipeChange(e, index, 'requiredItems')} required />
                </label>
                <label>
                  Quantity:
                  <input type="number" name={`requiredItem-quantity-${index}`} value={requiredItem.quantity} onChange={(e) => handleRecipeChange(e, index, 'requiredItems')} required />
                </label>
                <button type="button" onClick={() => removeRequiredItem(index)}>Remove</button>
              </div>
            ))}
            <button type="button" onClick={addRequiredItem}>Add Required Item</button>
            <button type="submit">Add Recipe</button>
          </form>
        )}
      </div>
    </div>
  );
};

export default DelayRender(AdminStore, 200);

